import { useState, useEffect } from 'react';
import { semantic } from '@/shared/theme/tokens';
import Slide from '@mui/material/Slide';

/**
 * Backdrop + slide-up card shell with enter/exit animation.
 *
 * Usage (internal open state — simplest):
 *   <SlideModal onClose={handleUnmount} maxWidth={560}>
 *     {content}
 *   </SlideModal>
 *
 * Usage (external open state — for parent-driven close):
 *   <SlideModal isOpen={open} onClose={() => setOpen(false)} onExited={() => setData(null)} maxWidth={560}>
 *     {content}
 *   </SlideModal>
 */
export function SlideModal({
  children,
  onClose,
  onExited,
  isOpen: isOpenProp,
  maxWidth = 560,
  maxHeight = '90vh',
  borderRadius = 14,
  zIndex = 1000,
  timeout = 220,
  padding = 16,
  fullHeight = false,
  c,
  isDark,
}) {
  const [internalOpen, setInternalOpen] = useState(true);
  const controlled = isOpenProp !== undefined;
  const isOpen = controlled ? isOpenProp : internalOpen;

  function handleClose() {
    if (controlled) {
      onClose?.();
    } else {
      setInternalOpen(false);
    }
  }

  function handleExited() {
    if (!controlled) onClose?.();
    onExited?.();
  }

  // Close on Escape while the modal is open.
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') handleClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // handleClose is stable enough for this lifecycle; re-bind only on open change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const bg = c?.bg ?? '#fff';
  const border = c?.border ?? 'rgba(0,0,0,0.1)';
  const shadow = isDark
    ? '0 8px 40px rgba(0,0,0,0.6)'
    : '0 8px 40px rgba(0,0,0,0.18)';

  return (
    <div
      onClick={(e) => e.target === e.currentTarget && handleClose()}
      style={{
        position: 'fixed', inset: 0, zIndex,
        background: semantic.overlay,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding,
      }}
    >
      <Slide in={isOpen} direction="up" timeout={timeout} onExited={handleExited}>
        <div
          role="dialog"
          aria-modal="true"
          onClick={(e) => e.stopPropagation()}
          style={{
            background: bg,
            border: `1px solid ${border}`,
            borderRadius,
            width: '100%',
            maxWidth,
            ...(fullHeight ? { height: maxHeight } : { maxHeight }),

            overflowY: fullHeight ? 'hidden' : 'auto',
            boxShadow: shadow,
            display: fullHeight ? 'flex' : undefined,
            flexDirection: fullHeight ? 'column' : undefined,
          }}
        >
          {children}
        </div>
      </Slide>
    </div>
  );
}
